/**
 * Canvas Registry Store (BYK-177)
 *
 * Provides a central registry for canvas references (Fabric.js and Babylon.js).
 * This allows the export service to access canvases without prop drilling.
 *
 * Usage:
 * - FloorPlanCanvas registers its fabric.Canvas on mount
 * - BabylonScene registers its engine/scene on mount
 * - Export actions can then access these refs
 */

import { create } from "zustand";
import type * as fabric from "fabric";
import type * as BABYLON from "@babylonjs/core";
import {
  export2DFloorPlan,
  export3DWhiteModel,
  export3DWithMaterials,
  combineImages,
  type ExportResult,
  type CanvasExportOptions,
  type WhiteModelOptions,
} from "@/services/canvasExport";

// ============================================================================
// Types
// ============================================================================

export interface BabylonRefs {
  engine: BABYLON.Engine;
  scene: BABYLON.Scene;
  shadowGenerator: BABYLON.ShadowGenerator | null;
  highlightLayer: BABYLON.HighlightLayer | null;
}

export interface ExportState {
  isExporting: boolean;
  exportProgress: number;
  lastExport: ExportResult | null;
  exportError: string | null;
}

interface CanvasRegistryState {
  // Canvas references
  fabricCanvas: fabric.Canvas | null;
  babylonRefs: BabylonRefs | null;

  // Export state
  exportState: ExportState;

  // Registration actions
  registerFabricCanvas: (canvas: fabric.Canvas | null) => void;
  registerBabylonRefs: (refs: BabylonRefs | null) => void;

  // Export actions
  export2D: (options?: CanvasExportOptions) => Promise<ExportResult | null>;
  export3DWhite: (options?: WhiteModelOptions) => Promise<ExportResult | null>;
  export3DTextured: (options?: CanvasExportOptions) => Promise<ExportResult | null>;
  exportCombined: (options?: CanvasExportOptions) => Promise<ExportResult | null>;

  // State management
  clearExportState: () => void;
}

// ============================================================================
// Default State
// ============================================================================

const DEFAULT_EXPORT_STATE: ExportState = {
  isExporting: false,
  exportProgress: 0,
  lastExport: null,
  exportError: null,
};

// ============================================================================
// Store
// ============================================================================

export const useCanvasRegistry = create<CanvasRegistryState>((set, get) => ({
  // Initial state
  fabricCanvas: null,
  babylonRefs: null,
  exportState: DEFAULT_EXPORT_STATE,

  // Registration actions
  registerFabricCanvas: (canvas) => {
    set({ fabricCanvas: canvas });
  },

  registerBabylonRefs: (refs) => {
    set({ babylonRefs: refs });
  },

  // Export 2D floor plan
  export2D: async (options = {}) => {
    const { fabricCanvas } = get();
    if (!fabricCanvas) {
      set({
        exportState: {
          ...DEFAULT_EXPORT_STATE,
          exportError: "2D canvas not available",
        },
      });
      return null;
    }

    set({
      exportState: {
        isExporting: true,
        exportProgress: 0,
        lastExport: null,
        exportError: null,
      },
    });

    try {
      set((state) => ({
        exportState: { ...state.exportState, exportProgress: 50 },
      }));

      const result = export2DFloorPlan(fabricCanvas, options);

      set({
        exportState: {
          isExporting: false,
          exportProgress: 100,
          lastExport: result,
          exportError: null,
        },
      });

      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Export failed";
      set({
        exportState: {
          isExporting: false,
          exportProgress: 0,
          lastExport: null,
          exportError: message,
        },
      });
      return null;
    }
  },

  // Export 3D white model
  export3DWhite: async (options = {}) => {
    const { babylonRefs } = get();
    if (!babylonRefs) {
      set({
        exportState: {
          ...DEFAULT_EXPORT_STATE,
          exportError: "3D scene not available",
        },
      });
      return null;
    }

    set({
      exportState: {
        isExporting: true,
        exportProgress: 0,
        lastExport: null,
        exportError: null,
      },
    });

    try {
      set((state) => ({
        exportState: { ...state.exportState, exportProgress: 30 },
      }));

      const result = await export3DWhiteModel(
        babylonRefs.engine,
        babylonRefs.scene,
        { ...options, useWhiteModel: true }
      );

      set({
        exportState: {
          isExporting: false,
          exportProgress: 100,
          lastExport: result,
          exportError: null,
        },
      });

      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Export failed";
      set({
        exportState: {
          isExporting: false,
          exportProgress: 0,
          lastExport: null,
          exportError: message,
        },
      });
      return null;
    }
  },

  // Export 3D with textures
  export3DTextured: async (options = {}) => {
    const { babylonRefs } = get();
    if (!babylonRefs) {
      set({
        exportState: {
          ...DEFAULT_EXPORT_STATE,
          exportError: "3D scene not available",
        },
      });
      return null;
    }

    set({
      exportState: {
        isExporting: true,
        exportProgress: 0,
        lastExport: null,
        exportError: null,
      },
    });

    try {
      set((state) => ({
        exportState: { ...state.exportState, exportProgress: 30 },
      }));

      const result = await export3DWithMaterials(
        babylonRefs.engine,
        babylonRefs.scene,
        options
      );

      set({
        exportState: {
          isExporting: false,
          exportProgress: 100,
          lastExport: result,
          exportError: null,
        },
      });

      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Export failed";
      set({
        exportState: {
          isExporting: false,
          exportProgress: 0,
          lastExport: null,
          exportError: message,
        },
      });
      return null;
    }
  },

  // Export combined 2D + 3D side by side
  exportCombined: async (options = {}) => {
    const { fabricCanvas, babylonRefs } = get();

    if (!fabricCanvas || !babylonRefs) {
      set({
        exportState: {
          ...DEFAULT_EXPORT_STATE,
          exportError: "Both 2D and 3D canvases required for combined export",
        },
      });
      return null;
    }

    set({
      exportState: {
        isExporting: true,
        exportProgress: 0,
        lastExport: null,
        exportError: null,
      },
    });

    try {
      // Export 2D
      set((state) => ({
        exportState: { ...state.exportState, exportProgress: 20 },
      }));
      const floorPlan = export2DFloorPlan(fabricCanvas, options);

      // Export 3D white model
      set((state) => ({
        exportState: { ...state.exportState, exportProgress: 50 },
      }));
      const whiteModel = await export3DWhiteModel(
        babylonRefs.engine,
        babylonRefs.scene,
        { ...options, useWhiteModel: true }
      );

      // Combine images
      set((state) => ({
        exportState: { ...state.exportState, exportProgress: 80 },
      }));
      const combined = await combineImages(floorPlan, whiteModel, options);

      set({
        exportState: {
          isExporting: false,
          exportProgress: 100,
          lastExport: combined,
          exportError: null,
        },
      });

      return combined;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Export failed";
      set({
        exportState: {
          isExporting: false,
          exportProgress: 0,
          lastExport: null,
          exportError: message,
        },
      });
      return null;
    }
  },

  // Clear export state
  clearExportState: () => {
    set({ exportState: DEFAULT_EXPORT_STATE });
  },
}));

export default useCanvasRegistry;
